import DimensionDO = App.Data.DimensionDO;
import DocumentDO = App.Data.Explorer.DocumentDO;
import DocumentVerticalDTO = App.Data.DocumentVerticalDTO;
import DocumentViewTabs = App.Enums.DocumentViewTabs;

/**
 * The business domains (parking, media, charging, …) a set of verticals covers.
 */
export const domainsOfVerticals = (
	verticals: (DocumentVerticalDTO | null | undefined)[]
): string[] => [
	...new Set(verticals.flatMap((vertical) => vertical?.domain ?? [])),
];

/** What the graph draws: the document's vertical plus the comparison one. */
export const domainsOfDocument = (document: DocumentDO | null): string[] =>
	domainsOfVerticals([document?.vertical, document?.secondary_vertical]);

/**
 * The domains in play in the view being looked at. The graph draws the
 * document's verticals, but the data table holds a metric per column — several
 * domains at once — and the single metric is picked out of that same table. A
 * document pins the filter menu to these, the way the axis configuration scopes
 * its dimension lists.
 */
export const domainsOfActiveView = (
	document: DocumentDO | null,
	activeDataView: DocumentViewTabs
): string[] => {
	if (activeDataView === "graph") {
		return domainsOfDocument(document);
	}

	const metrics = domainsOfVerticals(document?.data_tables?.[0]?.metrics ?? []);

	// A table without metrics says nothing about the domains in play, so fall
	// back to the verticals the document carries anyway.
	return metrics.length > 0 ? metrics : domainsOfDocument(document);
};

/** The domains on offer, in the order the dimensions arrived in. */
export const availableDomains = (dimensions: DimensionDO[]): string[] => [
	...new Set(dimensions.flatMap((dimension) => dimension.domain ?? [])),
];

/**
 * Narrows dimensions to the given domains. An empty scope means "every domain".
 *
 * Two things are deliberately never hidden: a dimension that carries no domains
 * at all (`domain` is null when the API hands us a dimension without its
 * property loaded), and anything named in `keep` — the dimensions of filters
 * that are already active, so editing one can't be blocked by the scope.
 */
export const scopeToDomains = (
	dimensions: DimensionDO[],
	domains: string[],
	keep: string[] = []
): DimensionDO[] =>
	domains.length === 0
		? dimensions
		: dimensions.filter(
				(dimension) =>
					!dimension.domain?.length ||
					keep.includes(dimension.name_clean) ||
					dimension.domain.some((domain) => domains.includes(domain))
		  );
